import { useEffect, useState, useRef } from "react"

const STORAGE_KEY = "global_search_history"

const GlobalInputSearch = ({
  value: initialValue,
  onChange,
  className = "",
  historyLimit = 500,
  dropdownLimit = 10,
  suggestions = [],
}) => {
  const [value, setValue] = useState(initialValue || "")
  const [history, setHistory] = useState([])
  const [isFocused, setIsFocused] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const wrapperRef = useRef(null)
  const dropdownRef = useRef(null)
  const itemRefs = useRef([])

  // Load local history
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")
    setHistory(saved)
  }, [])

  useEffect(() => setValue(initialValue || ""), [initialValue])

  const saveSearch = term => {
    if (!term.trim()) return
    const updated = [
      term.trim(),
      ...history.filter(h => h !== term.trim()),
    ].slice(0, historyLimit)
    setHistory(updated)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  }

  const handleKeyDown = e => {
    const items = sortedItems
    if (!items.length) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setHighlightIndex(prev => (prev + 1) % items.length)
      setIsFocused(true)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setHighlightIndex(prev => (prev - 1 + items.length) % items.length)
      setIsFocused(true)
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (highlightIndex >= 0 && items[highlightIndex]) {
        const selected = items[highlightIndex].value
        setValue(selected)
        onChange(selected)
        saveSearch(selected)
      } else {
        saveSearch(value)
        onChange(value)
      }
      setIsFocused(false)
      setHighlightIndex(-1)
    } else if (e.key === "Escape") {
      e.preventDefault()
      setIsFocused(false)
      setHighlightIndex(-1)
    }
  }

  const handleClickSearch = () => {
    saveSearch(value)
    onChange(value)
    setIsFocused(false)
    setHighlightIndex(-1)
  }

  const handleChange = e => {
    setValue(e.target.value)
    setIsFocused(true)
    setHighlightIndex(-1)
  }

  const handleClear = () => {
    setValue("")
    onChange("")
    setIsFocused(true)
    setHighlightIndex(-1)
  }

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsFocused(false)
        setHighlightIndex(-1)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const recentHistory = history.slice(0, dropdownLimit)
  const dataListItems = [
    ...recentHistory.map(item => ({ value: item, type: "history" })),
    ...suggestions.map(item => ({ value: item, type: "suggestion" })),
  ]

  const seen = new Set()
  const uniqueItems = dataListItems.filter(item => {
    if (seen.has(item.value)) return false
    seen.add(item.value)
    return true
  })

  const sortedItems = [...uniqueItems].sort((a, b) => {
    if (!value) return 0
    const aMatch = a.value.toLowerCase().includes(value.toLowerCase())
    const bMatch = b.value.toLowerCase().includes(value.toLowerCase())
    if (aMatch && !bMatch) return -1
    if (!aMatch && bMatch) return 1
    return 0
  })

  const highlightMatch = (text, query) => {
    if (!query) return text
    const regex = new RegExp(
      `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    )
    const parts = text.split(regex)
    return parts.map((part, idx) =>
      regex.test(part) ? (
        <mark
          key={idx}
          style={{
            backgroundColor: "#ffe58f",
            padding: 0,
            borderRadius: "0.15rem",
          }}
        >
          {part}
        </mark>
      ) : (
        <span key={idx}>{part}</span>
      )
    )
  }

  // Auto-scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && itemRefs.current[highlightIndex]) {
      const item = itemRefs.current[highlightIndex]
      const container = dropdownRef.current
      if (item && container) {
        const itemTop = item.offsetTop
        const itemBottom = item.offsetTop + item.offsetHeight
        const containerTop = container.scrollTop
        const containerBottom = container.scrollTop + container.offsetHeight

        if (itemTop < containerTop) container.scrollTop = itemTop
        else if (itemBottom > containerBottom)
          container.scrollTop = itemBottom - container.offsetHeight
      }
    }
  }, [highlightIndex, sortedItems])

  return (
    <div
      className="position-relative"
      style={{ maxWidth: "450px" }}
      ref={wrapperRef}
    >
      {/* Input + search + clear */}
      <div
        className="position-relative shadow-sm rounded overflow-hidden"
        style={{ display: "flex", maxWidth: "450px" }}
      >
        <input
          className={`form-control border-0 ${className}`}
          placeholder="Search..."
          value={value ?? ""}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          style={{
            flex: 1,
            padding: "0.6rem 2.8rem 0.6rem 0.75rem",
            fontSize: "0.95rem",
            borderRadius: 0,
          }}
        />

        {/* Clear button inside input */}
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="btn p-0 d-flex align-items-center justify-content-center"
            style={{
              position: "absolute",
              right: "2.8rem", // space before search button
              top: "50%",
              transform: "translateY(-50%)",
              width: "1.8rem",
              height: "1.8rem",
              borderRadius: "50%",
              background: "transparent",
              border: "none",
              color: "#6c757d",
              cursor: "pointer",
            }}
          >
            <i className="bx bx-x" style={{ fontSize: "1.2rem" }}></i>
          </button>
        )}

        {/* Search button */}
        <button
          className="btn btn-primary d-flex align-items-center justify-content-center"
          type="button"
          onClick={handleClickSearch}
          style={{ padding: "0 0.8rem", borderRadius: 0 }}
        >
          <i className="bx bx-search"></i>
        </button>
      </div>

      {/* Badge dropdown */}
      {isFocused && sortedItems.length > 0 && (
        <div
          className="position-absolute w-100 mt-2 p-2 bg-white shadow-lg rounded"
          style={{
            maxHeight: "220px",
            overflowY: "auto",
            zIndex: 1000,
            animation: "fadeIn 0.15s ease-out",
            position: "relative",
          }}
          ref={dropdownRef}
        >
          <div className="dropdown-fade-top"></div>
          <div className="d-flex flex-wrap gap-2 position-relative">
            {sortedItems.map((item, idx) => {
              const isHistory = item.type === "history"
              const isHighlighted = idx === highlightIndex
              return (
                <span
                  key={idx}
                  ref={el => (itemRefs.current[idx] = el)}
                  onMouseDown={() => {
                    setValue(item.value)
                    onChange(item.value)
                    saveSearch(item.value)
                    setIsFocused(true)
                    setHighlightIndex(-1)
                  }}
                  className={`badge d-flex align-items-center`}
                  style={{
                    cursor: "pointer",
                    maxWidth: "100%",
                    padding: "0.45rem 0.75rem",
                    fontSize: "0.875rem",
                    transition: "all 0.2s",
                    backgroundColor: isHighlighted
                      ? "#0d6efd"
                      : isHistory
                      ? "#6c757d"
                      : "#17a2b8",
                    color: isHighlighted ? "#fff" : isHistory ? "#fff" : "#000",
                    transform: "scale(1)",
                    boxShadow: isHighlighted
                      ? "0 4px 15px rgba(0,0,0,0.2)"
                      : "0 0 0 rgba(0,0,0,0)",
                  }}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  onMouseLeave={() => setHighlightIndex(-1)}
                >
                  <i
                    className={`bx ${
                      isHistory ? "bx-history me-1" : "bx-star me-1"
                    }`}
                  />
                  {highlightMatch(item.value, value)}
                </span>
              )
            })}
          </div>
          <div className="dropdown-fade-bottom"></div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(-5px);} to {opacity:1; transform:translateY(0);} }
        mark { border-radius: 0.15rem; }
        .dropdown-fade-top, .dropdown-fade-bottom {
          position: absolute; left:0; right:0; height:20px; pointer-events:none; z-index:2;
        }
        .dropdown-fade-top { top:0; background: linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,0)); }
        .dropdown-fade-bottom { bottom:0; background: linear-gradient(to top, rgba(255,255,255,1), rgba(255,255,255,0)); }
        .badge:hover { transform: scale(1.05); box-shadow:0 6px 12px rgba(0,0,0,0.18); }
      `}</style>
    </div>
  )
}

export default GlobalInputSearch
